// Тулбар форматирования над Milkdown редактором.
//
// Все block- и mark-кнопки — TOGGLE (Notion/Linear-стиль):
//   - H1 на параграфе → станет H1
//   - H1 на уже-H1 → станет параграфом (для всех textblocks в селекции)
//   - Quote/List внутри уже Quote/List → выйдет наружу
//   - Inline marks при курсоре внутри марка → расширяет до границ и снимает
//
// 🧹 Eraser — снять ВСЁ форматирование за ОДНУ транзакцию (Tiptap-стиль).
//   Block + inline в одном dispatch — без визуальной задержки и race с listener'ом.

import { memo } from 'react';
import { useInstance } from '@milkdown/react';
import { callCommand } from '@milkdown/utils';
import { editorViewCtx } from '@milkdown/core';
import { toggleLinkCommand, insertHrCommand } from '@milkdown/preset-commonmark';
import { insertTableCommand } from '@milkdown/preset-gfm';
import { undoCommand, redoCommand } from '@milkdown/plugin-history';
import {
  setBlockType,
  lift,
  wrapIn,
  toggleMark,
} from '@milkdown/prose/commands';
import { wrapInList, liftListItem } from '@milkdown/prose/schema-list';
import { liftTarget } from '@milkdown/prose/transform';
import {
  deleteTable,
  deleteRow,
  deleteColumn,
  isInTable,
  addRowAfter as addRowAfterCmd,
  addColumnAfter as addColumnAfterCmd,
} from '@milkdown/prose/tables';
import type { EditorView } from '@milkdown/prose/view';
import type { NodeType, MarkType } from '@milkdown/prose/model';
import type { Editor } from '@milkdown/core';
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Minus,
  Link as LinkIcon,
  Undo2,
  Redo2,
  FileCode2,
  Table as TableIcon,
  Pilcrow,
  Plus,
  X,
  Eraser,
} from 'lucide-react';

interface ToolbarBtnProps {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  className?: string;
}

const ToolbarBtn = memo(function ToolbarBtn({ onClick, title, children, className }: ToolbarBtnProps) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className={
        className ??
        'p-1.5 rounded text-ink-muted_l dark:text-ink-muted_d hover:bg-surface3-light dark:hover:bg-surface3-dark hover:text-accent transition'
      }
    >
      {children}
    </button>
  );
});

function Divider() {
  return <span className="w-px h-5 bg-line-light dark:bg-line-dark mx-0.5" />;
}

// Узел определённого типа есть в предках выделения?
function isInsideNode(view: EditorView, typeName: string): boolean {
  const { $from } = view.state.selection;
  for (let d = $from.depth; d > 0; d--) {
    if ($from.node(d).type.name === typeName) return true;
  }
  return false;
}

// Расширяет позицию до границ непрерывного диапазона данного марка.
// Inline-атомы без марок (wikilinks, mentions) НЕ разрывают диапазон.
function getMarkRange(
  doc: import('@milkdown/prose/model').Node,
  pos: number,
  markType: MarkType,
): { from: number; to: number } | null {
  const $pos = doc.resolve(pos);
  const parent = $pos.parent;
  const parentStart = $pos.start();
  const offsetInParent = $pos.parentOffset;

  let childIdx = -1;
  let childPos = parentStart;
  for (let i = 0; i < parent.childCount; i++) {
    const child = parent.child(i);
    const next = childPos + child.nodeSize;
    if (offsetInParent < next - parentStart) {
      childIdx = i;
      break;
    }
    childPos = next;
  }
  if (childIdx === -1) {
    childIdx = parent.childCount - 1;
    if (childIdx < 0) return null;
    childPos = parentStart;
    for (let i = 0; i < childIdx; i++) childPos += parent.child(i).nodeSize;
  }

  const child = parent.child(childIdx);
  if (!markType.isInSet(child.marks)) return null;

  // Walk back. Atom-ноды без марок (wikilinks) считаем «прозрачными» — не разрывают.
  let from = childPos;
  for (let i = childIdx - 1; i >= 0; i--) {
    const prev = parent.child(i);
    if (prev.isAtom && !prev.isText && prev.marks.length === 0) {
      from -= prev.nodeSize;
      continue;
    }
    if (!markType.isInSet(prev.marks)) break;
    from -= prev.nodeSize;
  }
  let to = childPos + child.nodeSize;
  for (let i = childIdx + 1; i < parent.childCount; i++) {
    const next = parent.child(i);
    if (next.isAtom && !next.isText && next.marks.length === 0) {
      to += next.nodeSize;
      continue;
    }
    if (!markType.isInSet(next.marks)) break;
    to += next.nodeSize;
  }
  return { from, to };
}

export const DocumentToolbar = memo(function DocumentToolbar() {
  const [loading, getEditor] = useInstance();

  const run = (cmdKey: Parameters<typeof callCommand>[0], payload?: unknown) => {
    if (loading) return;
    const editor = getEditor();
    if (!editor) return;
    editor.action(callCommand(cmdKey, payload));
  };

  const pm = (fn: (view: EditorView, editor: Editor) => void) => {
    if (loading) return;
    const editor = getEditor();
    if (!editor) return;
    editor.action((ctx) => {
      const view = ctx.get(editorViewCtx);
      fn(view, editor);
      view.focus();
    });
  };

  const toggleInlineMark = (markName: 'strong' | 'emphasis' | 'strike_through' | 'inlineCode') => {
    pm((view) => {
      const schema = view.state.schema;
      const mark: MarkType | undefined = schema.marks[markName];
      if (!mark) {
        console.warn(`[Toolbar] Mark "${markName}" not found. Available:`, Object.keys(schema.marks));
        return;
      }
      const { state } = view;
      const { selection, doc } = state;
      const { from, to, empty } = selection;

      if (!empty) {
        const has = doc.rangeHasMark(from, to, mark);
        if (has) {
          view.dispatch(state.tr.removeMark(from, to, mark).scrollIntoView());
        } else {
          view.dispatch(state.tr.addMark(from, to, mark.create()).scrollIntoView());
        }
        return;
      }

      const $from = selection.$from;
      const rangeRight = getMarkRange(doc, $from.pos, mark);
      const rangeLeft = $from.pos > 0 ? getMarkRange(doc, $from.pos - 1, mark) : null;
      const range = rangeRight ?? rangeLeft;

      if (range && range.from !== range.to) {
        view.dispatch(state.tr.removeMark(range.from, range.to, mark).scrollIntoView());
        return;
      }
      toggleMark(mark)(state, view.dispatch);
    });
  };

  // Toggle heading: проверяем ВСЕ textblock'и в селекции, не только $from.parent.
  const toggleHeading = (level: number) => {
    pm((view) => {
      const schema = view.state.schema;
      const heading: NodeType | undefined = schema.nodes.heading;
      const paragraph: NodeType | undefined = schema.nodes.paragraph;
      if (!heading || !paragraph) return;

      const { from, to } = view.state.selection;
      let allSameHeading = true;
      let textblockCount = 0;
      view.state.doc.nodesBetween(from, to, (node) => {
        if (node.isTextblock) {
          textblockCount++;
          if (node.type !== heading || node.attrs.level !== level) {
            allSameHeading = false;
          }
        }
      });
      if (textblockCount === 0) return;

      const target = allSameHeading ? paragraph : heading;
      const attrs = allSameHeading ? null : { level };
      setBlockType(target, attrs)(view.state, view.dispatch);
    });
  };

  const toggleCodeBlock = () => {
    pm((view) => {
      const schema = view.state.schema;
      const codeBlock: NodeType | undefined = schema.nodes.code_block;
      const paragraph: NodeType | undefined = schema.nodes.paragraph;
      if (!codeBlock || !paragraph) return;
      const node = view.state.selection.$from.parent;
      const target = node.type === codeBlock ? paragraph : codeBlock;
      setBlockType(target)(view.state, view.dispatch);
    });
  };

  const toggleBlockquote = () => {
    pm((view) => {
      const schema = view.state.schema;
      const blockquote: NodeType | undefined = schema.nodes.blockquote;
      const codeBlock: NodeType | undefined = schema.nodes.code_block;
      if (!blockquote) return;

      // Не оборачиваем code_block в blockquote — markdown round-trip ненадёжен.
      if (codeBlock && view.state.selection.$from.parent.type === codeBlock) {
        console.warn('[Toolbar] Нельзя обернуть code-block в цитату — сначала ¶ снять code-block');
        return;
      }

      if (isInsideNode(view, 'blockquote')) {
        lift(view.state, view.dispatch);
      } else {
        wrapIn(blockquote)(view.state, view.dispatch);
      }
    });
  };

  // List toggle с авто-конверсией heading → paragraph (heading не разрешён в list_item).
  const toggleList = (which: 'bullet_list' | 'ordered_list') => {
    pm((view) => {
      const schema = view.state.schema;
      const listType: NodeType | undefined = schema.nodes[which];
      const listItem: NodeType | undefined = schema.nodes.list_item;
      const paragraph: NodeType | undefined = schema.nodes.paragraph;
      if (!listType || !listItem || !paragraph) return;

      if (isInsideNode(view, which)) {
        liftListItem(listItem)(view.state, view.dispatch);
        return;
      }

      // Если в heading или code-block — сначала переключим в paragraph.
      const parent = view.state.selection.$from.parent;
      const isHeading = parent.type.name === 'heading';
      const isCodeBlock = parent.type.name === 'code_block';
      if (isHeading || isCodeBlock) {
        const ok = setBlockType(paragraph)(view.state, view.dispatch);
        if (!ok) {
          console.warn(`[Toolbar] не смогли превратить ${parent.type.name} в paragraph для wrap в ${which}`);
          return;
        }
      }
      const ok = wrapInList(listType)(view.state, view.dispatch);
      if (!ok) console.warn(`[Toolbar] wrapInList(${which}) returned false`);
    });
  };

  // ¶ Превратить в параграф — за один step. Семантика: «выйти на один уровень
  // наружу ИЛИ превратить heading/code-block в paragraph».
  const clearBlockFormatting = () => {
    pm((view) => {
      const schema = view.state.schema;
      const paragraph: NodeType | undefined = schema.nodes.paragraph;
      const listItem: NodeType | undefined = schema.nodes.list_item;
      if (!paragraph) return;

      // Шаг 1: попробовать lift из list_item.
      if (listItem && liftListItem(listItem)(view.state, view.dispatch)) return;
      // Шаг 2: lift из любого другого контейнера (quote/callout).
      if (lift(view.state, view.dispatch)) return;
      // Шаг 3: уже на root — превращаем в paragraph если heading/code-block.
      if (view.state.selection.$from.parent.type !== paragraph) {
        setBlockType(paragraph)(view.state, view.dispatch);
      }
    });
  };

  // 🧹 СНЯТЬ ВСЁ ФОРМАТИРОВАНИЕ — ОДНА транзакция (Tiptap-style).
  //   Pattern из @tiptap/core/clearNodes + unsetAllMarks: собираем tr,
  //   обходим nodesBetween, для каждого textblock'а — setNodeMarkup(paragraph)
  //   + lift до корня. Все марки удаляются по селекции.
  //   Одно view.dispatch — нет race condition, нет визуальной задержки.
  //
  //   Сохраняется: текст, wikilinks/tags (это ноды, не марки).
  //   Удаляется: bold/italic/strike/code/link, heading/code-block обёртка,
  //              list/quote/callout обёртки.
  const clearAllFormatting = () => {
    pm((view) => {
      const { state } = view;
      const schema = state.schema;
      const paragraph: NodeType | undefined = schema.nodes.paragraph;
      if (!paragraph) return;

      // Не трогаем структуру в таблице (CellSelection) — иначе превратим cell в paragraph.
      const isCellSel = state.selection.constructor.name === 'CellSelection';

      let tr = state.tr;
      const sel = state.selection;

      // === 1. Удалить ВСЕ inline-марки на range'ах селекции ===
      // Если empty — берём весь текущий блок.
      let markFrom: number;
      let markTo: number;
      if (sel.empty) {
        markFrom = sel.$from.start();
        markTo = sel.$from.end();
      } else {
        markFrom = sel.from;
        markTo = sel.to;
      }
      // removeMark без 3-го аргумента удаляет ВСЕ марки в диапазоне.
      tr = tr.removeMark(markFrom, markTo);

      // === 2. Lift всех textblock'ов до корня + setNodeMarkup в paragraph ===
      if (!isCellSel) {
        const blocks: { from: number; to: number; depth: number }[] = [];
        const blockFrom = sel.empty ? sel.$from.before() : sel.from;
        const blockTo = sel.empty ? sel.$from.after() : sel.to;
        state.doc.nodesBetween(blockFrom, blockTo, (node, pos) => {
          if (node.isTextblock) {
            blocks.push({ from: pos, to: pos + node.nodeSize, depth: 0 });
          }
        });
        // С конца к началу — позиции не съезжают при последовательных lift'ах.
        for (let i = blocks.length - 1; i >= 0; i--) {
          const original = blocks[i];
          const mappedFrom = tr.mapping.map(original.from);
          const node = tr.doc.nodeAt(mappedFrom);
          if (!node || !node.isTextblock) continue;
          // setNodeMarkup → paragraph (если не уже).
          if (node.type !== paragraph) {
            tr = tr.setNodeMarkup(mappedFrom, paragraph, null, []);
          }
          // Lift до корня — пока есть target.
          for (let attempt = 0; attempt < 8; attempt++) {
            const $start = tr.doc.resolve(tr.mapping.map(original.from) + 1);
            const $end = tr.doc.resolve(
              Math.min(tr.mapping.map(original.to) - 1, tr.doc.content.size - 1),
            );
            const range = $start.blockRange($end);
            if (!range) break;
            const target = liftTarget(range);
            if (target == null) break;
            tr = tr.lift(range, target);
          }
        }
      }

      if (tr.docChanged || tr.steps.length > 0) {
        view.dispatch(tr.scrollIntoView());
      }
    });
  };

  // Команды таблицы.
  const tableCmd = (cmd: (state: EditorView['state'], dispatch: EditorView['dispatch']) => boolean) => {
    pm((view) => {
      if (!isInTable(view.state)) return;
      cmd(view.state, view.dispatch);
    });
  };

  const onLink = () => {
    if (loading) return;
    const url = window.prompt('URL ссылки:');
    if (!url) return;
    run(toggleLinkCommand.key, { href: url });
  };

  return (
    <div className="flex items-center flex-wrap gap-0.5 mb-3 p-1 rounded-lg border border-line-light dark:border-line-dark bg-surface2-light/80 dark:bg-surface2-dark/80 backdrop-blur sticky top-0 z-10">
      <ToolbarBtn onClick={() => run(undoCommand.key)} title="Отменить (Ctrl+Z)">
        <Undo2 size={15} />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => run(redoCommand.key)} title="Повторить (Ctrl+Shift+Z)">
        <Redo2 size={15} />
      </ToolbarBtn>

      <Divider />

      <ToolbarBtn onClick={() => toggleHeading(1)} title="Заголовок 1 (повторно — снять)">
        <Heading1 size={15} />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => toggleHeading(2)} title="Заголовок 2 (повторно — снять)">
        <Heading2 size={15} />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => toggleHeading(3)} title="Заголовок 3 (повторно — снять)">
        <Heading3 size={15} />
      </ToolbarBtn>
      <ToolbarBtn onClick={clearBlockFormatting} title="Превратить в параграф (снять heading / выйти из списка-цитаты)">
        <Pilcrow size={15} />
      </ToolbarBtn>

      <Divider />

      <ToolbarBtn onClick={() => toggleInlineMark('strong')} title="Жирный (Ctrl+B)">
        <Bold size={15} />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => toggleInlineMark('emphasis')} title="Курсив (Ctrl+I)">
        <Italic size={15} />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => toggleInlineMark('strike_through')} title="Зачёркнутый">
        <Strikethrough size={15} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => toggleInlineMark('inlineCode')}
        title="Inline код — фиолетовый тег (Ctrl+E). Курсор внутри тега снимет."
      >
        <Code size={15} />
      </ToolbarBtn>
      <ToolbarBtn onClick={toggleCodeBlock} title="Блок кода (повторно — снять)">
        <FileCode2 size={15} />
      </ToolbarBtn>

      <Divider />

      <ToolbarBtn
        onClick={() => toggleList('bullet_list')}
        title="Маркированный список (повторно — выйти)"
      >
        <List size={15} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => toggleList('ordered_list')}
        title="Нумерованный список (повторно — выйти)"
      >
        <ListOrdered size={15} />
      </ToolbarBtn>
      <ToolbarBtn onClick={toggleBlockquote} title="Цитата (повторно — выйти)">
        <Quote size={15} />
      </ToolbarBtn>

      <Divider />

      <ToolbarBtn onClick={() => run(insertTableCommand.key)} title="Вставить таблицу 3×3">
        <TableIcon size={15} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => tableCmd(addRowAfterCmd)}
        title="Добавить строку"
      >
        <span className="flex items-center" style={{ fontSize: 11 }}>
          <Plus size={11} />R
        </span>
      </ToolbarBtn>
      <ToolbarBtn onClick={() => tableCmd(addColumnAfterCmd)} title="Добавить колонку">
        <span className="flex items-center" style={{ fontSize: 11 }}>
          <Plus size={11} />C
        </span>
      </ToolbarBtn>
      <ToolbarBtn onClick={() => tableCmd(deleteRow)} title="Удалить строку">
        <span className="flex items-center" style={{ fontSize: 11 }}>
          <X size={11} />R
        </span>
      </ToolbarBtn>
      <ToolbarBtn onClick={() => tableCmd(deleteColumn)} title="Удалить колонку">
        <span className="flex items-center" style={{ fontSize: 11 }}>
          <X size={11} />C
        </span>
      </ToolbarBtn>
      <ToolbarBtn onClick={() => tableCmd(deleteTable)} title="Удалить таблицу">
        <span className="flex items-center" style={{ fontSize: 11 }}>
          <X size={11} />T
        </span>
      </ToolbarBtn>

      <Divider />

      <ToolbarBtn onClick={onLink} title="Ссылка">
        <LinkIcon size={15} />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => run(insertHrCommand.key)} title="Горизонтальная линия">
        <Minus size={15} />
      </ToolbarBtn>

      {/* Spacer выталкивает Eraser к правому краю */}
      <div className="flex-1" />

      {/* Деструктивная кнопка — отдельный визуальный блок справа.
          Красноватый цвет, чтобы пользователь видел «это убирает всё». */}
      <div className="flex items-center pl-2 ml-1 border-l-2 border-red-200/40 dark:border-red-900/40">
        <ToolbarBtn
          onClick={clearAllFormatting}
          title="Снять ВСЁ форматирование (block + inline) — текст сохранится. Аналог Cmd+\\ в Google Docs."
          className="p-1.5 rounded text-red-600/70 dark:text-red-400/70 hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-600 dark:hover:text-red-400 transition flex items-center gap-1"
        >
          <Eraser size={15} />
          <span className="text-[11px] font-medium pr-0.5">Снять всё</span>
        </ToolbarBtn>
      </div>
    </div>
  );
});
